import { Loader2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { getErrorMessage } from "../../lib/query/errors";
import { saveExitSelection } from "./api/saveExitSelection";
import { useExitOptions } from "./api/useExitOptions";

interface ExitAccountModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Deal / approval identifier — path param for the options read + save. */
  approvalId: string;
  /**
   * Current status description (from the application-status read). Shown as the
   * read-only "Current Account Status" and passed to the options query as the
   * `currentStatus` the recap-service transitions are computed from.
   */
  currentStatus?: string;
  /** Current phase description (from the application-status read); display only. */
  currentPhase?: string;
  /** Analyst role name — required query param for the options read. */
  roleName?: string;
  /**
   * Saves the applicant data (the same save the footer's Save button runs).
   * Save & Exit awaits this and only proceeds to exit when it resolves
   * `true`; a `false` (validation failure, save error, or a force-exit
   * block) keeps the modal open so the analyst sees why.
   */
  onSaveApplicant: () => Promise<boolean>;
  /**
   * Performs the actual exit side-effects: releases the held VLA lock and
   * navigates to the exit target. Invoked after a Save & Exit (once the
   * applicant save succeeds) or immediately for an Exit-without-saving.
   */
  onExit: () => void | Promise<void>;
}

/**
 * Picks the value to seed a dropdown with. Returns the first preferred value
 * that is actually one of the available options; otherwise the first option (or
 * "" when there are none). This guards against seeding a Select with a value
 * that has no matching item — e.g. the deal's current status is often not among
 * the allowed *transitions* — which would render the dropdown blank while still
 * holding a truthy value the analyst never saw selected.
 * @param {string[] | undefined} list - The available option values.
 * @param {...(string | undefined)} preferred - Candidate values in priority order.
 * @returns {string} The value to seed the dropdown with.
 */
function pickSeed(list: string[] | undefined, ...preferred: Array<string | undefined>): string {
  const match = preferred.find((value) => value !== undefined && value !== "" && list?.includes(value));
  return match ?? list?.[0] ?? "";
}

// "Exit the Account" modal, in parity with Step 6's exit screen. Shows the
// deal's current status/phase, lets the analyst pick the next status/phase to
// move the account to (options fetched from the Workbench exit-options BFF), and
// confirms the exit. Ticking "Exit without saving" skips the save and turns the
// confirm button into a plain Exit.
//
// The next status/phase persistence is a placeholder for now (see
// saveExitSelection); the release-lock + navigate side-effects are real and
// handled by the `onExit` callback supplied by the page.
function ExitAccountModal({
  open,
  onOpenChange,
  approvalId,
  currentStatus,
  currentPhase,
  roleName,
  onSaveApplicant,
  onExit,
}: ExitAccountModalProperties) {
  const { t } = useTranslation();
  const [nextStatus, setNextStatus] = useState("");
  const [nextPhase, setNextPhase] = useState("");
  const [exitWithoutSaving, setExitWithoutSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string>();

  // Only fetch options while the modal is open and the required inputs exist.
  const {
    data: options,
    isLoading,
    isError,
    error,
  } = useExitOptions(approvalId, currentStatus ?? "", roleName ?? "", { enabled: open });

  // Seed the dropdowns from the response once it lands — preferring the current
  // status/phase when they're valid choices, else the first available option —
  // without clobbering a selection the analyst changed.
  useEffect(() => {
    if (!options) {
      return;
    }
    setNextStatus((previous) => previous || pickSeed(options.nextStatusList, options.currentStatus));
    setNextPhase((previous) => previous || pickSeed(options.phasesList, options.manualPhase, options.currentPhase));
  }, [options]);

  // Reset transient state whenever the modal closes so reopening starts clean.
  useEffect(() => {
    if (open) {
      return;
    }
    setNextStatus("");
    setNextPhase("");
    setExitWithoutSaving(false);
    setIsSubmitting(false);
    setSaveError(undefined);
  }, [open]);

  const handleConfirm = useCallback(async () => {
    setIsSubmitting(true);
    setSaveError(undefined);
    try {
      if (!exitWithoutSaving) {
        const saved = await onSaveApplicant();
        if (!saved) {
          // Signers' own driver/secondSigner banners already show the specific
          // reason (validation, save error, or force-exit warning) — this is
          // just the modal-local summary. Stay open; don't exit.
          setSaveError(t("exit.saveError"));
          return;
        }
        // Placeholder — no persistence endpoint exists yet (see saveExitSelection).
        await saveExitSelection({ approvalId, nextStatus, nextPhase });
      }
      await onExit();
    } finally {
      setIsSubmitting(false);
    }
  }, [exitWithoutSaving, onSaveApplicant, approvalId, nextStatus, nextPhase, onExit, t]);

  const handleSendFundingFax = useCallback(() => {
    // TODO: wire to the funding-fax action once the endpoint/asset is available.
  }, []);

  const statusList = options?.nextStatusList ?? [];
  const phaseList = options?.phasesList ?? [];
  // When saving, both selections are required; disable confirm until they exist
  // (or until the options finish loading). Exit-without-saving is always allowed.
  const confirmDisabled = isSubmitting || (!exitWithoutSaving && (isLoading || !nextStatus || !nextPhase));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="gap-0 overflow-hidden p-0 sm:max-w-md">
        {/* Header — navy bar with title + close. */}
        <div className="bg-primary text-primary-foreground flex items-center justify-between border-b px-4 py-2">
          <DialogTitle className="text-primary-foreground text-sm font-semibold">{t("exit.title")}</DialogTitle>
          <DialogDescription className="sr-only">{t("exit.description")}</DialogDescription>
          <DialogClose className="text-primary-foreground/80 hover:text-primary-foreground p-0.5 transition-colors">
            <X className="size-4.5" />
            <span className="sr-only">{t("exit.close")}</span>
          </DialogClose>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-3 p-4">
          {/* Current account status (read-only) */}
          <section>
            <h4 className="text-primary mb-1 text-xs font-semibold">{t("exit.currentStatus.heading")}</h4>
            <div className="border-border bg-muted grid grid-cols-2 gap-2 rounded-md border p-2">
              <div>
                <div className="text-muted-foreground text-xs">{t("exit.fields.status")}</div>
                <div className="text-primary text-xs">{currentStatus || t("exit.notAvailable")}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">{t("exit.fields.phase")}</div>
                <div className="text-primary text-xs">{currentPhase || t("exit.notAvailable")}</div>
              </div>
            </div>
          </section>

          {/* Move account to (editable) */}
          <section>
            <h4 className="text-primary mb-1 text-xs font-semibold">{t("exit.moveTo.heading")}</h4>
            {isError ? (
              <p role="alert" className="text-destructive text-xs">
                {t("exit.moveTo.loadError", { message: getErrorMessage(error) })}
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-muted-foreground mb-1 text-xs font-normal">{t("exit.fields.status")}</Label>
                  <Select value={nextStatus} onValueChange={setNextStatus} disabled={isLoading || exitWithoutSaving}>
                    <SelectTrigger className="w-full bg-white">
                      <SelectValue placeholder={isLoading ? t("exit.moveTo.loading") : t("exit.moveTo.selectStatus")} />
                    </SelectTrigger>
                    <SelectContent>
                      {statusList.map((status) => (
                        <SelectItem key={status} value={status}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-muted-foreground mb-1 text-xs font-normal">{t("exit.fields.phase")}</Label>
                  <Select value={nextPhase} onValueChange={setNextPhase} disabled={isLoading || exitWithoutSaving}>
                    <SelectTrigger className="w-full bg-white">
                      <SelectValue placeholder={isLoading ? t("exit.moveTo.loading") : t("exit.moveTo.selectPhase")} />
                    </SelectTrigger>
                    <SelectContent>
                      {phaseList.map((phase) => (
                        <SelectItem key={phase} value={phase}>
                          {phase}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </section>

          {/* Exit without saving */}
          <label className="flex cursor-pointer items-center gap-2">
            <Checkbox
              className="bg-white"
              checked={exitWithoutSaving}
              onCheckedChange={(checked) => setExitWithoutSaving(checked === true)}
            />
            <span className="text-primary text-xs">{t("exit.exitWithoutSaving")}</span>
          </label>
        </div>

        {/* Footer */}
        <div className="bg-muted flex flex-col gap-2 border-t px-4 py-3">
          {saveError && (
            <p role="alert" className="text-destructive text-xs">
              {saveError}
            </p>
          )}
          <Button type="button" size="sm" disabled={confirmDisabled} onClick={() => void handleConfirm()}>
            {isSubmitting && <Loader2 className="size-4 animate-spin" />}
            {exitWithoutSaving ? t("exit.exit") : t("exit.saveAndExit")}
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="flex-1"
              disabled={isSubmitting}
              onClick={handleSendFundingFax}
            >
              {t("exit.sendFundingFax")}
            </Button>
            <DialogClose asChild>
              <Button type="button" variant="outline" size="sm" className="flex-1" disabled={isSubmitting}>
                {t("exit.cancel")}
              </Button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ExitAccountModal;
